import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listGuests, addGuestFlag } from "@/lib/admin.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Flag } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/guests")({
  component: GuestsPage,
});

const CATEGORIES = ["no_show", "late_cancel", "complaint", "vip", "manager_review", "banned"] as const;

function GuestsPage() {
  const [search, setSearch] = useState("");
  const [flagFor, setFlagFor] = useState<string | null>(null);
  const [cat, setCat] = useState<(typeof CATEGORIES)[number]>("no_show");
  const [reason, setReason] = useState("");

  const fn = useServerFn(listGuests);
  const flagFn = useServerFn(addGuestFlag);
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({
    queryKey: ["guests", search],
    queryFn: () => fn({ data: { search } }),
  });

  const mut = useMutation({
    mutationFn: async () => {
      if (!flagFor || !reason.trim()) throw new Error("Reason required.");
      await flagFn({ data: { guest_id: flagFor, category: cat, reason: reason.trim() } });
    },
    onSuccess: () => {
      toast.success("Flag added");
      setFlagFor(null); setReason(""); setCat("no_show");
      qc.invalidateQueries({ queryKey: ["guest-flags"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Guests</h1>
          <p className="mt-1 text-sm text-muted-foreground">Search guests and add Booking Control flags.</p>
        </div>
        <Input placeholder="Search name, email or phone…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" />
      </div>

      <div className="rounded-2xl border border-border bg-card/30">
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : data.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No guests found.</div>
        ) : (
          <ul className="divide-y divide-border">
            {data.map((g) => (
              <li key={g.id} className="grid items-center gap-3 px-5 py-3 md:grid-cols-[1fr_200px_140px_120px]">
                <div>
                  <div className="text-sm font-semibold">{g.full_name}</div>
                  <div className="text-xs text-muted-foreground">{g.email ?? "—"}</div>
                </div>
                <span className="text-sm">{g.phone}</span>
                <span className="text-xs uppercase tracking-wider text-primary">{g.status}</span>
                <Button size="sm" variant="ghost" onClick={() => setFlagFor(g.id)}>
                  <Flag size={14} className="mr-1" /> Flag
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {flagFor && (
        <form
          onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}
          className="rounded-2xl border border-border bg-card/40 p-5"
        >
          <div className="text-sm font-semibold">New flag</div>
          <div className="mt-3 grid gap-3 md:grid-cols-[200px_1fr_auto]">
            <select value={cat} onChange={(e) => setCat(e.target.value as never)} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
            </select>
            <Input placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
            <div className="flex gap-2">
              <Button type="submit" disabled={mut.isPending}>Save flag</Button>
              <Button type="button" variant="ghost" onClick={() => setFlagFor(null)}>Cancel</Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
